import React from 'react';

/**
 * App-owned store context used to replace mobx-react's Provider/inject.
 *
 * Value shape:
 * {
 *   routing,
 *   authStore,
 *   uiState,
 *   fileUploadStore,
 * }
 */
export const StoresContext = React.createContext(null);

export function StoresProvider({ value, children }) {
  return (
    <StoresContext.Provider value={value}>{children}</StoresContext.Provider>
  );
}

/**
 * Bridge for class components (and HOC-heavy exports): injects stores as props.
 *
 * The mapping intentionally matches the historic mobx-react inject(...) behavior
 * so that existing components can keep using `this.props.authStore` etc.
 */
export function withStores(WrappedComponent) {
  const WithStores = props => (
    <StoresContext.Consumer>
      {stores => {
        if (!stores) {
          throw new Error(
            'StoresContext is missing. Wrap your app in <StoresProvider value={stores}>.'
          );
        }

        return (
          <WrappedComponent
            {...props}
            routing={stores.routing}
            authStore={stores.authStore}
            uiState={stores.uiState}
            fileUploadStore={stores.fileUploadStore}
          />
        );
      }}
    </StoresContext.Consumer>
  );

  WithStores.displayName = `withStores(${WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component'})`;

  return WithStores;
}
